import type { CarFuel, EventosInputs, IntraCityMode, LodgingType } from '../api/types';
import { Field, RadioChip, SelectInput, TextInput } from './FormControls';

interface Props {
  value: EventosInputs;
  onChange: (patch: Partial<EventosInputs>) => void;
  onBack: () => void;
  onNext: () => void;
  errors: Partial<Record<keyof EventosInputs, string>>;
}

const LODGING_OPTIONS: { value: LodgingType; label: string; hint?: string }[] = [
  { value: 'hotel', label: 'Hotel' },
  { value: 'hostel', label: 'Hostel' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'casa_amigos', label: 'Casa de amigos / família', hint: 'Sem custo de hospedagem' },
  { value: 'proprio', label: 'Imóvel próprio' },
  { value: 'nao_me_hospedei', label: 'Não me hospedei', hint: 'Vim no dia' },
];

const INTRA_OPTIONS: { value: IntraCityMode; label: string }[] = [
  { value: 'a_pe', label: 'A pé' },
  { value: 'bicicleta', label: 'Bicicleta' },
  { value: 'uber_taxi', label: 'Uber, táxi, app' },
  { value: 'metro_onibus', label: 'Metrô, ônibus' },
  { value: 'carro_proprio', label: 'Carro próprio' },
];

const CAR_FUELS: { value: CarFuel; label: string }[] = [
  { value: 'gasolina', label: 'Gasolina' },
  { value: 'etanol', label: 'Etanol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'eletrico', label: 'Elétrico' },
];

export function StepHospedagem({ value, onChange, onBack, onNext, errors }: Props) {
  const showIntraCarFuel = value.intra_city_transport === 'carro_proprio';
  const lodgingNeeded =
    value.lodging_type && value.lodging_type !== 'nao_me_hospedei' && value.lodging_type !== 'casa_amigos';

  return (
    <form
      className="space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        onNext();
      }}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Dias de evento" required error={errors.event_days}>
          <TextInput
            type="number"
            inputMode="numeric"
            min={0}
            max={31}
            value={value.event_days || ''}
            onChange={(e) => onChange({ event_days: parseInt(e.target.value, 10) || 0 })}
            placeholder="Ex.: 3"
          />
        </Field>
        <Field
          label="Noites de hospedagem"
          hint={lodgingNeeded ? undefined : 'Opcional'}
          error={errors.lodging_nights}
        >
          <TextInput
            type="number"
            inputMode="numeric"
            min={0}
            max={31}
            value={value.lodging_nights || ''}
            onChange={(e) => onChange({ lodging_nights: parseInt(e.target.value, 10) || 0 })}
            placeholder="Ex.: 2"
          />
        </Field>
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-tabaco mb-2">
          Onde você se hospedou?
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {LODGING_OPTIONS.map((opt) => (
            <RadioChip
              key={opt.value}
              name="lodging_type"
              value={opt.value}
              checked={value.lodging_type === opt.value}
              onChange={(v) => onChange({ lodging_type: v as LodgingType })}
              label={opt.label}
              hint={opt.hint}
            />
          ))}
        </div>
        {errors.lodging_type && (
          <p className="text-2xs text-cobre-700 mt-1.5">{errors.lodging_type}</p>
        )}
      </div>

      <div>
        <p className="text-xs uppercase tracking-wider text-tabaco mb-2">
          Como você se deslocou na cidade durante o evento?
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {INTRA_OPTIONS.map((opt) => (
            <RadioChip
              key={opt.value}
              name="intra_city_transport"
              value={opt.value}
              checked={value.intra_city_transport === opt.value}
              onChange={(v) => onChange({ intra_city_transport: v as IntraCityMode })}
              label={opt.label}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field
          label="Km percorridos por dia"
          hint="Aproximado — só conta o deslocamento intra-cidade"
        >
          <TextInput
            type="number"
            inputMode="decimal"
            min={0}
            step="0.5"
            value={value.intra_city_km_per_day || ''}
            onChange={(e) =>
              onChange({ intra_city_km_per_day: parseFloat(e.target.value) || 0 })
            }
            placeholder="Ex.: 15"
          />
        </Field>
        {showIntraCarFuel && (
          <Field label="Combustível do carro" required error={errors.intra_city_car_fuel}>
            <SelectInput
              value={value.intra_city_car_fuel ?? ''}
              onChange={(e) =>
                onChange({ intra_city_car_fuel: (e.target.value || undefined) as CarFuel })
              }
            >
              <option value="">Selecione…</option>
              {CAR_FUELS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </SelectInput>
          </Field>
        )}
      </div>

      <div className="pt-2 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-tabaco hover:text-floresta transition-colors"
        >
          ← Voltar
        </button>
        <button
          type="submit"
          className="px-5 py-2.5 rounded-md bg-floresta text-cream text-sm font-medium hover:bg-floresta-600 transition-colors"
        >
          Calcular emissão →
        </button>
      </div>
    </form>
  );
}
